import { MemberExportType } from "../../pages/AdminStatistics/AdminStatistics";
import { OccupancyAggregationType } from "../../shared/enums";

export const getOccupancySlotsQueryFn = (
  start_date: string,
  end_date: string,
  types: OccupancyAggregationType[],
  format: string,
  filters?: {
    courtIds?: string[];
    permissionIds?: string[];
    gender?: string;
    timeRange?: { start_time?: string; end_time?: string };
    ageRange?: { min?: number; max?: number };
    bookingStatuses?: string[];
  },
  selectedWeekdays?: number[],
) => {
  const query = `
         getOccupancySlots(
        start_date: $start_date,
        end_date: $end_date,
        types: $types,
        filters: $filters,
        selectedWeekdays: $selectedWeekdays,
        format: $format
      ) {
        __typename
        ... on StatisticsExport {
          type
          count
          generatedAt
          data {
            __typename
            ... on MemberTypeOccupancyRow {
              type
              memberType
              week
              year
              monday { booker participant activity filteredBookings systemBookings systemCapacity }
              tuesday { booker participant activity filteredBookings systemBookings systemCapacity }
              wednesday { booker participant activity filteredBookings systemBookings systemCapacity }
              thursday { booker participant activity filteredBookings systemBookings systemCapacity }
              friday { booker participant activity filteredBookings systemBookings systemCapacity }
              saturday { booker participant activity filteredBookings systemBookings systemCapacity }
              sunday { booker participant activity filteredBookings systemBookings systemCapacity }
              total { booker participant activity filteredBookings systemBookings systemCapacity }
            }
            ... on GuestOccupancyRow {
              type
              memberType
              week
              year
              monday { onlyGuest total }
              tuesday { onlyGuest total }
              wednesday { onlyGuest total }
              thursday { onlyGuest total }
              friday { onlyGuest total }
              saturday { onlyGuest total }
              sunday { onlyGuest total }
              total { onlyGuest total }
            }
            ... on CourtOccupancyRow {
              type
              aktuellt_datum
              total_slots
              utfall_23
              utfall_09
              one_day_09
              two_days_09
              three_days_09
              four_days_09
              five_days_09
            }
          }
        }
        ... on DataExport {
              url
        }
        ... on Error {
          code
          message
        }
      }
`;
  const inputs = `
    $start_date: String!,
    $end_date: String!,
    $types: [OccupancyAggregationType!]!,
    $filters: OccupancyFilterInput,
    $selectedWeekdays: [Int!]!,
    $format: String!
  `;
  const variables = {
    start_date: start_date,
    end_date: end_date,
    types: types,
    filters: filters ?? null,
    selectedWeekdays: selectedWeekdays ?? [],
    format: format,
  };

  return {
    variables,
    inputs,
    query,
  };
};

export const getRehabOccupancySlotsQueryFn = (
  start_date: string,
  end_date: string,
  format: string,
  filters?: {
    permissionIds?: string[];
    genders?: string[];
    ageRange?: { min?: number; max?: number };
  },
  selectedWeekdays?: number[],
) => {
  const query = `
    getRehabOccupancySlots(
      start_date: $start_date,
      end_date: $end_date,
      format: $format,
      filters: $filters,
      selectedWeekdays: $selectedWeekdays
    ) {
      __typename
      ... on RehabStatisticsExport {
        count
        generatedAt
        data {
          __typename
          ... on RehabOccupancyRow {
            type
            member_name
            permission_id
            timespan
            bookings_count
            total_count
            available_count
          }
        }
      }
      ... on Error {
        code
        message
      }
    }
  `;

  const inputs = `
    $start_date: String!,
    $end_date: String!,
    $format: String!,
    $filters: OccupancyFilterInput,
    $selectedWeekdays: [Int!]!
  `;

  const variables = {
    start_date,
    end_date,
    format: format,
    filters: filters ?? null,
    selectedWeekdays: selectedWeekdays ?? [],
  };

  return {
    variables,
    inputs,
    query,
  };
};

export const getCourtOccupancySlotsQueryFn = (
  start_date: string,
  end_date: string,
  format: string,
  filters?: {
    courtIds?: string[];
    permissionIds?: string[];
    genders?: string[];
    timeRange?: { start_time?: string; end_time?: string };
    ageRange?: { min?: number; max?: number };
    typeOfBookings?: string[];
  },
  selectedWeekdays?: number[],
) => {
  const query = `
    getCourtOccupancySlots(
      start_date: $start_date,
      end_date: $end_date,
      format: $format,
      filters: $filters,
      selectedWeekdays: $selectedWeekdays
    ) {
      __typename
      ... on CourtStatisticsExport {
        generatedAt
        count
        data {
          __typename
          type
          courtName
          courtId
          timespan
          monday {
            total_slots
            booked { amount percent }
            blue { amount percent }
            red { amount percent }
          }
          tuesday {
            total_slots
            booked { amount percent }
            blue { amount percent }
            red { amount percent }
          }
          wednesday {
            total_slots
            booked { amount percent }
            blue { amount percent }
            red { amount percent }
          }
          thursday {
            total_slots
            booked { amount percent }
            blue { amount percent }
            red { amount percent }
          }
          friday {
            total_slots
            booked { amount percent }
            blue { amount percent }
            red { amount percent }
          }
          saturday {
            total_slots
            booked { amount percent }
            blue { amount percent }
            red { amount percent }
          }
          sunday {
            total_slots
            booked { amount percent }
            blue { amount percent }
            red { amount percent }
          }
          average {
            total_slots
            booked { amount percent }
            blue { amount percent }
            red { amount percent }
          }
        }
      }
      ... on Error {
        code
        message
      }
    }
  `;

  const inputs = `
    $start_date: String!,
    $end_date: String!,
    $format: String!,
    $filters: OccupancyFilterInput,
    $selectedWeekdays: [Int!]
  `;

  const variables = {
    start_date,
    end_date,
    format: format,
    filters: filters ?? null,
    selectedWeekdays: selectedWeekdays ?? [],
  };

  return {
    query,
    variables,
    inputs,
  };
};

export const getMemberOccupancySlotsQueryFn = (
  start_date: string,
  end_date: string,
  format: string,
  filters?: {
    courtIds?: string[];
    permissionIds?: string[];
    genders?: string[];
    timeRange?: { start_time?: string; end_time?: string };
    ageRange?: { min?: number; max?: number };
    bookingStatuses?: string[];
  },
  selectedWeekdays?: number[],
) => {
  const query = `
      getMemberOccupancySlots(
        start_date: $start_date,
        end_date: $end_date,
        format: $format,
        filters: $filters,
        selectedWeekdays: $selectedWeekdays
      ) {
        __typename
        ... on MemberStatisticsExport {
          count
          generatedAt
          data {
            __typename
            ... on MemberOccupancyRow { 
             type
              name
              memberType
              timespan
              monday { booker participant activity }
              tuesday { booker participant activity }
              wednesday { booker participant activity }
              thursday { booker participant activity }
              friday { booker participant activity }
              saturday { booker participant activity }
              sunday { booker participant activity }
              total { booker participant activity }
            }
            
          }
        }
        ... on Error {
          code
          message
        }
      }
`;
  const inputs = `
    $start_date: String!,
    $end_date: String!,
    $format: String!,
    $filters: OccupancyFilterInput,
    $selectedWeekdays: [Int!]
  `;
  const variables = {
    start_date: start_date,
    end_date: end_date,
    format: format,
    filters: filters ?? null,
    selectedWeekdays: selectedWeekdays ?? [],
  };

  return {
    variables,
    inputs,
    query,
  };
};

export const getNoShowOccupancySlotsQueryFn = (
  start_date: string,
  end_date: string,
  format: string,
  filters?: {
    permissionIds?: string[];
    genders?: string[];
    ageRange?: { min?: number; max?: number };
  },
) => {
  const query = `
    getNoShowOccupancySlots(
      start_date: $start_date,
      end_date: $end_date,
      format: $format,
      filters: $filters
    ) {
      __typename
      ... on NoShowStatisticsExport {
        generatedAt
        count
        data {
          __typename
          ... on NoShowOccupancyRow {
            type
            name
            memberType
            timespan
            activeNoshows
            totalNoshows
            seminoshows
          }
        }
      }
      ... on Error {
        code
        message
      }
    }
  `;

  const inputs = `
    $start_date: String!,
    $end_date: String!,
    $format: String!,
    $filters: OccupancyFilterInput
  `;

  const variables = {
    start_date,
    end_date,
    format: format,
    filters: filters ?? null,
  };

  return { query, variables, inputs };
};

export const getActivityOccupancySlotsQueryFn = (
  start_date: string,
  end_date: string,
  format: string,
  filters?: {
    courtIds?: string[];
    permissionIds?: string[];
    genders?: string[];
    ageRange?: { min?: number; max?: number };
    timeRange?: { start_time?: string; end_time?: string };
  },
  selectedWeekdays?: number[],
) => {
  const query = `
    getActivityOccupancySlots(
      start_date: $start_date,
      end_date: $end_date,
      format: $format,
      filters: $filters,
      selectedWeekdays: $selectedWeekdays
    ) {
      __typename
      ... on ActivityStatisticsExport {
        generatedAt
        count
        activities {
          activityName
          rows {
            __typename
            type
            timespan
            monday {
              booked
              capacity
              fullyBookedSessions
              totalSessions
              waitlistCount
              memberTypes { memberType count }
              waitlistMemberTypes { memberType count }
            }
            tuesday {
              booked
              capacity
              fullyBookedSessions
              totalSessions
              waitlistCount
              memberTypes { memberType count }
              waitlistMemberTypes { memberType count }
            }
            wednesday {
              booked
              capacity
              fullyBookedSessions
              totalSessions
              waitlistCount
              memberTypes { memberType count }
              waitlistMemberTypes { memberType count }
            }
            thursday {
              booked
              capacity
              fullyBookedSessions
              totalSessions
              waitlistCount
              memberTypes { memberType count }
              waitlistMemberTypes { memberType count }
            }
            friday {
              booked
              capacity
              fullyBookedSessions
              totalSessions
              waitlistCount
              memberTypes { memberType count }
              waitlistMemberTypes { memberType count }
            }
            saturday {
              booked
              capacity
              fullyBookedSessions
              totalSessions
              waitlistCount
              memberTypes { memberType count }
              waitlistMemberTypes { memberType count }
            }
            sunday {
              booked
              capacity
              fullyBookedSessions
              totalSessions
              waitlistCount
              memberTypes { memberType count }
              waitlistMemberTypes { memberType count }
            }

            total {
              booked
              capacity
              fullyBookedSessions
              totalSessions
              waitlistCount
              memberTypes { memberType count }
              waitlistMemberTypes { memberType count }
            }
          }
        }
      }

      ... on Error {
        code
        message
      }
    }
  `;

  const inputs = `
    $start_date: String!,
    $end_date: String!,
    $format: String!,
    $filters: OccupancyFilterInput,
    $selectedWeekdays: [Int!]
  `;

  const variables = {
    start_date,
    end_date,
    format: format,
    filters: filters ?? null,
    selectedWeekdays: selectedWeekdays ?? [],
  };

  return { query, variables, inputs };
};

export const getCoachOccupancySlotsQueryFn = (
  start_date: string,
  end_date: string,
  format: string,
  filters?: {
    courtIds?: string[];
    permissionIds?: string[];
    genders?: string[];
    timeRange?: { start_time?: string; end_time?: string };
    ageRange?: { min?: number; max?: number };
    coachIds?: string[];
  },
  selectedWeekdays?: number[],
) => {
  const query = `
    getCoachOccupancySlots(
      start_date: $start_date,
      end_date: $end_date,
      format: $format,
      filters: $filters,
      selectedWeekdays: $selectedWeekdays
    ) {
      __typename
      ... on CoachStatisticsExport {
        generatedAt
        count
        coaches {
          coachId
          coachName
          rows {
            __typename
            type
            timespan
            monday {
              booked
              memberTypes { memberType count }
            }
            tuesday {
              booked
              memberTypes { memberType count }
            }
            wednesday {
              booked
              memberTypes { memberType count }
            }
            thursday {
              booked
              memberTypes { memberType count }
            }
            friday {
              booked
              memberTypes { memberType count }
            }
            saturday {
              booked
              memberTypes { memberType count }
            }
            sunday {
              booked
              memberTypes { memberType count }
            }

            total {
              booked
              memberTypes { memberType count }
            }
          }
        }
      }
      ... on Error {
        code
        message
      }
    }
  `;

  const inputs = `
    $start_date: String!,
    $end_date: String!,
    $format: String!,
    $filters: OccupancyFilterInput,
    $selectedWeekdays: [Int!]
  `;

  const variables = {
    start_date,
    end_date,
    format: format,
    filters: filters ?? null,
    selectedWeekdays: selectedWeekdays ?? [],
  };

  return {
    query,
    variables,
    inputs,
  };
};

export const exportStatisticsOccupancySlotsQueryFn = (
  export_resolver: string,
  exportType: OccupancyAggregationType | MemberExportType,
  startDate: string,
  endDate: string,
  fileName: string,
  filters?: {
    courtIds?: string[];
    permissionIds?: string[];
    genders?: string[];
    timeRange?: { start_time?: string; end_time?: string };
    ageRange?: { min?: number; max?: number };
    bookingStatuses?: string[];
    activityColors?: string[];
    coachIds?: string[];
  },
  selectedWeekdays?: number[],
) => {
  const query = `
    startOccupancyStatisticsExport(
      export_resolver: $export_resolver
      exportType: $exportType
      startDate: $startDate
      endDate: $endDate
      fileName: $fileName
      filters: $filters
      selectedWeekdays: $selectedWeekdays
    ) {
      __typename
      success
      message
    }
  `;

  const inputs = `
    $export_resolver: String!
    $exportType: OccupancyAggregationType! 
    $startDate: String!
    $endDate: String!
    $fileName: String!
    $filters: OccupancyFilterInput
    $selectedWeekdays: [Int!]
  `;

  const variables = {
    export_resolver,
    exportType,
    startDate,
    endDate,
    fileName,
    filters: filters ?? null,
    selectedWeekdays: selectedWeekdays ?? [],
  };

  return {
    query,
    inputs,
    variables,
  };
};

export const listStatisticsExportsQueryFn = () => {
  const query = `
    listStatisticsExports {
      __typename
      ... on ListStatisticsExportsResponse {
        items {
          id
          fileName
          size
          lastModified
          downloadUrl
        }
      }
      ... on Error {
        code
        message
      }
    }
  `;

  const inputs = ``;
  const variables = {};

  return {
    query,
    inputs,
    variables,
  };
};

export const deleteStatisticsExportQueryFn = (exportId: string) => {
  const query = `
    deleteStatisticsExport(exportId: $exportId) {
      __typename
      success
    }
  `;

  const inputs = `$exportId: String!`;
  const variables = { exportId };

  return {
    query,
    inputs,
    variables,
  };
};
